<template>
  <div>
    <v-sheet class="body-1 mx-3" v-if="!basePosition && segments.length === 0">
      ({{ $tt('caster.noTrackData') }})
    </v-sheet>
    <div v-else>
      <gmap-map
        ref="mapRef"
        :center="center"
        :zoom="15"
        map-type-id="hybrid"
        :options="{
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
        }"
        :style="{ height: '480px', width: '100%' }"
      >
        <gmap-marker v-if="basePosition" :position="basePosition" :title="$tt('caster.baseShort')" />
        <gmap-polyline
          v-for="segment in segments"
          :key="segment.key"
          :path="segment.path"
          :options="{ strokeColor: segment.color, strokeWeight: 4, strokeOpacity: 0.9 }"
        />
        <gmap-marker
          v-for="rover in roverMarkers"
          :key="rover.key"
          :position="rover.position"
          :label="rover.label"
          :title="rover.title"
        />
      </gmap-map>

      <div class="mt-2">
        <v-chip class="mr-2 mb-1" small outlined v-for="item in legendItems" :key="item.label" :color="item.color">
          {{ item.label }}
        </v-chip>
      </div>
      <div class="caption grey--text" v-for="rover in roverCaptions" :key="rover.key">
        {{ rover.text }}
      </div>
    </div>
  </div>
</template>

<script>
/* global google */
import i18n from '@/lib/i18n'

const QUALITY_COLORS = {
  4: '#4CAF50', // RTK FIX
  5: '#FF9800', // RTK FLOAT
  2: '#2196F3', // DGPS
  1: '#F44336', // 単独測位
}
const DEFAULT_COLOR = '#9E9E9E'

/**
 * 地図タブ: 基準局マーカー + 移動局のGGA軌跡 (fix品質で色分け) + 基線長
 */
export default {
  name: 'CasterTrackMap',
  props: {
    snapshot: { type: Object, default: null }, // CasterStatsSnapshot (基準局位置)
    track: { type: Object, default: null }, // MountpointTrack
  },
  computed: {
    basePosition() {
      const position = this.snapshot && this.snapshot.position
      if (!position) {
        return null
      }
      return { lat: position.lat, lng: position.lon }
    },
    rovers() {
      return (this.track && this.track.rovers) || []
    },
    segments() {
      const segments = []
      this.rovers.forEach((rover, roverIndex) => {
        const points = rover.track || []
        let current = null
        points.forEach(point => {
          const color = QUALITY_COLORS[point.quality] || DEFAULT_COLOR
          if (!current || current.color !== color) {
            // 色が変わっても線が繋がるように、前のセグメントの最後の点を先頭に引き継ぐ
            const head = current ? [current.path[current.path.length - 1]] : []
            current = { key: `${roverIndex}-${segments.length}`, color, path: head.slice() }
            segments.push(current)
          }
          current.path.push({ lat: point.lat, lng: point.lon })
        })
      })
      return segments.filter(segment => segment.path.length >= 2)
    },
    roverMarkers() {
      return this.rovers
        .map((rover, index) => {
          const point = rover.stats && rover.stats.lastPoint
          if (!point) {
            return null
          }
          return {
            key: `rover-${index}`,
            position: { lat: point.lat, lng: point.lon },
            label: 'R',
            title: rover.clientId || rover.roverId,
          }
        })
        .filter(marker => marker !== null)
    },
    roverCaptions() {
      return this.rovers
        .map((rover, index) => {
          const point = rover.stats && rover.stats.lastPoint
          if (!point) {
            return null
          }
          const baseline =
            point.baselineM !== undefined ? `${i18n.tt('caster.baseline')} ${Math.round(point.baselineM)}m` : ''
          const quality = `${i18n.tt('caster.fixQuality')} ${this.qualityLabel(point.quality)}`
          const pointsCount = `${i18n.tt('caster.ggaPoints')} ${rover.stats.recordedPoints}`
          return {
            key: `caption-${index}`,
            text: `${rover.clientId || rover.roverId}: ${quality} / ${baseline} / ${pointsCount}`,
          }
        })
        .filter(caption => caption !== null)
    },
    legendItems() {
      return [
        { label: 'RTK FIX', color: QUALITY_COLORS[4] },
        { label: 'RTK FLOAT', color: QUALITY_COLORS[5] },
        { label: 'DGPS', color: QUALITY_COLORS[2] },
        { label: 'GPS', color: QUALITY_COLORS[1] },
      ]
    },
    center() {
      if (this.basePosition) {
        return this.basePosition
      }
      const firstSegment = this.segments[0]
      if (firstSegment) {
        return firstSegment.path[0]
      }
      return { lat: 35.681236, lng: 139.767125 }
    },
  },
  watch: {
    track() {
      this.fitBounds()
    },
  },
  mounted() {
    this.fitBounds()
  },
  methods: {
    qualityLabel(quality) {
      switch (quality) {
        case 4:
          return 'FIX'
        case 5:
          return 'FLOAT'
        case 2:
          return 'DGPS'
        case 1:
          return 'GPS'
        default:
          return `${quality}`
      }
    },
    fitBounds() {
      if (!this.$refs.mapRef) {
        return
      }
      const positions = []
      if (this.basePosition) {
        positions.push(this.basePosition)
      }
      this.segments.forEach(segment => positions.push(...segment.path))
      if (positions.length < 2) {
        return
      }
      this.$refs.mapRef.$mapPromise.then(map => {
        const bounds = new google.maps.LatLngBounds()
        positions.forEach(position => bounds.extend(position))
        map.fitBounds(bounds)
      })
    },
  },
}
</script>
